import { useEffect, useMemo, useRef } from "react";
import { Quill } from "react-quill";
import { User, useGetMentionUsersQuery } from "../../../__generated__/api";
import Styles from "./Editor.module.scss";

// eslint-disable-next-line import/no-extraneous-dependencies
import "quill-mention";

import DefaultAvatar from "../../../assets/img/logo-reverse-big.svg";

const MentionBlot = Quill.import("blots/mention");

function handleError(this: HTMLImageElement) {
    this.src = DefaultAvatar;
}

function createCard(data: User, className: string) {
    const div = document.createElement("mention");
    div.setAttribute("data-id", data.id as string);
    div.setAttribute("data-type", "mention");
    div.setAttribute("data-name", data.name as string);
    div.setAttribute("data-avatarUrl", data.avatarUrl as string);
    div.contentEditable = "false";
    div.classList.add(className);
    const avatar = document.createElement("img");
    avatar.src = data.avatarUrl ?? DefaultAvatar ?? "";
    avatar.onerror = handleError;
    avatar.classList.add("avatar", "avatar-xxs", "rounded");
    div.append(avatar);
    div.append(data.name as string);
    return div;
}

class UserMentionBlot extends MentionBlot {
    static render(data: User) {
        return createCard(data, Styles["mention-display"]);
    }
}
UserMentionBlot.blotName = "mention";

Quill.register(UserMentionBlot);

export default function useMentions({
    sourceId,
}: {
    sourceId: string;
}) {
    const { data } = useGetMentionUsersQuery({ skip: !sourceId, variables: { id: sourceId } });
    const dataRef = useRef<User[]>();
    useEffect(() => {
        if (!data) {
            return;
        }
        // const usersInChannel = new Set<string>(data.conversationSourceUsers?.toIdList("id"));
        dataRef.current = data.conversationSourceUsers?.concat(data.settings?.agents ?? []).dedupe(row => row.id) as User[];
    }, [data]);
    return useMemo(() => ({
        mention: {
            allowedChars: /^[A-Za-z\s0-9 ]*$/,
            mentionDenotationChars: ["@"],
            blotName: "mention",
            dataAttributes: ["id", "name", "avatarUrl", "type"],
            mentionContainerClass: Styles["mentions-container"],
            mentionListClass: Styles["mentions-list"],
            defaultMenuOrientation: "top",
            isolateCharacter: true,
            showDenotationChar: false,
            renderItem: (item: User) => createCard(item, Styles.mention),
            source(
                searchTerm: string,
                renderList: ((list: User[], term: string) => void),
            ) {
                if (!searchTerm) {
                    renderList([] as User[], searchTerm);
                } else {
                    const search = searchTerm.toLowerCase();
                    const users = dataRef.current?.filter(user => user.firstName?.toLowerCase().includes(search)
                        || user.lastName?.toLowerCase().includes(search)
                        || user.handles?.some(handle => handle.email?.email?.toLowerCase().includes(search)))
                        .limit(10)
                        .map(user => ({
                            ...user,
                            type: "mention",
                        }));

                    renderList(users ?? [], searchTerm);
                }
            },
        },
    }), []);
}
